import { http } from '../../utils/http'
import conf from '../../config'

export const API_ROOT = `${conf.API_URL}`

export function newTierId() {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
        return crypto.randomUUID()
    }
    return `tier-${Date.now()}-${Math.random().toString(36).slice(2, 11)}`
}

/**
 * Ensure product and every T1–T4 node has a sibling `id`. Does not regenerate existing ids.
 * Mutates and returns the product for convenient chaining.
 */
export function ensureProductTierIds(product) {
    if (!product) return product
    if (!product.id) product.id = newTierId()
    ;(product.tier_1_data || []).forEach(t1 => {
        if (!t1.id) t1.id = newTierId()
        ;(t1.tier_2_data || []).forEach(t2 => {
            if (!t2.id) t2.id = newTierId()
            ;(t2.tier_3_data || []).forEach(t3 => {
                if (!t3.id) t3.id = newTierId()
                ;(t3.tier_4_data || []).forEach(t4 => {
                    if (!t4.id) t4.id = newTierId()
                })
            })
        })
    })
    return product
}

export function ensureProductsTierIds(products) {
    const list = Array.isArray(products) ? products : []
    list.forEach(ensureProductTierIds)
    return list
}

function mapTier4ToUpsert(t4) {
    return {
        values: { ...(t4.values || {}) }
    }
}

function mapTier3ToUpsert(t3) {
    return {
        values: { ...(t3.values || {}) },
        tier4Data: (t3.tier_4_data || []).map(mapTier4ToUpsert)
    }
}

function mapTier2ToUpsert(t2) {
    return {
        values: { ...(t2.values || {}) },
        tier3Data: (t2.tier_3_data || []).map(mapTier3ToUpsert)
    }
}

function mapTier1ToUpsert(t1) {
    return {
        values: { ...(t1.values || {}) },
        tier2Data: (t1.tier_2_data || []).map(mapTier2ToUpsert)
    }
}

function mapProductToUpsert(product) {
    if (!product) return { name: '', tier1Data: [] }
    const productId = product.product_id ?? product.productId ?? product.id ?? null
    return {
        productId: productId && Number(productId) > 0 ? Number(productId) : null,
        name: product.name || '',
        // Only meaningful for recall-response products (ResponseFormProducts); the general
        // recall/communication products upsert path has no such columns and ignores them.
        quantity: product.quantity !== undefined && product.quantity !== null && product.quantity !== ''
            ? Number(product.quantity)
            : null,
        unitOfMeasure: product.unitOfMeasure || null,
        tier1Data: (product.tier_1_data || []).map(mapTier1ToUpsert)
    }
}

/**
 * Maps snake_case tier product list (UI / GET response shape) to the
 * camelCase UpsertProducts request payload.
 */
export function toUpsertPayload(products) {
    const list = Array.isArray(products) ? products : []
    return {
        products: list.map(mapProductToUpsert)
    }
}

function splitValuesAndChildren(node, childKeys) {
    const existing = { ...(node.values || {}) }
    const rest = {}
    Object.keys(node || {}).forEach(key => {
        // Keep structural keys out of the values bag (including sibling id)
        if (key === 'values' || key === 'id' || childKeys.includes(key)) return
        if (node[key] != null && node[key] !== '') {
            // migrate legacy manufactured_* aliases
            let fieldKey = key
            if (key === 'manufactured_start_date') fieldKey = 'manufacturing_start_date'
            if (key === 'manufactured_end_date') fieldKey = 'manufacturing_end_date'
            if (key === 'ref_no_list_no') fieldKey = 'ref_list_no'
            existing[fieldKey] = typeof node[key] === 'object' && node[key].$date
                ? node[key].$date
                : node[key]
        }
    })
    return { values: existing, rest }
}

function resolveNodeId(node) {
    return (node && typeof node.id === 'string' && node.id) ? node.id : newTierId()
}

function normalizeTier4Node(t4) {
    const { values } = splitValuesAndChildren(t4, [])
    return {
        id: resolveNodeId(t4),
        values
    }
}

function normalizeTier3Node(t3) {
    const { values } = splitValuesAndChildren(t3, ['tier_4_data'])
    return {
        id: resolveNodeId(t3),
        values,
        tier_4_data: (t3.tier_4_data || []).map(normalizeTier4Node)
    }
}

function normalizeTier2Node(t2) {
    const { values } = splitValuesAndChildren(t2, ['tier_3_data'])
    return {
        id: resolveNodeId(t2),
        values,
        tier_3_data: (t2.tier_3_data || []).map(normalizeTier3Node)
    }
}

function normalizeTier1Node(t1) {
    const { values, rest } = splitValuesAndChildren(t1, ['tier_2_data', 'tier_3_data'])
    return {
        id: resolveNodeId(t1),
        values,
        tier_2_data: (t1.tier_2_data || rest.tier_2_data || []).map(normalizeTier2Node)
    }
}

/**
 * Normalize GET response products into the UI shape with values bags.
 * Also migrates legacy tier nodes that stored fields as top-level props.
 * Preserves/generates sibling `id` on product and each tier node (Mongo); does not send ids to SQL upsert.
 */
export function normalizeProductsFromApi(products) {
    const list = Array.isArray(products) ? products : []
    return list.map(p => {
        const productId = p.product_id ?? p.productId ?? null
        const numericFromId = (p.id != null
            && !Number.isNaN(Number(p.id))
            && Number(p.id) > 0
            && /^\d+$/.test(String(p.id).trim()))
            ? Number(p.id)
            : null

        const result = {
            name: p.name || '',
            quantity: p.quantity ?? '',
            unitOfMeasure: p.unitOfMeasure || '',
            tier_1_data: (p.tier_1_data || []).map(normalizeTier1Node)
        }

        if (productId != null && Number(productId) > 0) {
            result.product_id = Number(productId)
        } else if (numericFromId) {
            result.product_id = numericFromId
        }

        // Preserve non-numeric Mongo UUID; generate for Mongo-shaped products without one
        if (typeof p.id === 'string' && p.id && !/^\d+$/.test(p.id.trim())) {
            result.id = p.id
        } else if (!result.product_id) {
            result.id = newTierId()
        }

        return result
    })
}

/**
 * Convert a legacy flat Mongo product_list item into nested values bags.
 * Known FieldKeys are placed into inferred tiers when definitions are unavailable.
 */
export function convertFlatProductToTier(product, fieldDefinitions = []) {
    if (!product) return { id: newTierId(), name: '', tier_1_data: [] }
    if (product.tier_1_data) {
        return normalizeProductsFromApi([product])[0]
    }

    const byKeyTier = {}
    ;(fieldDefinitions || []).forEach(d => {
        if (d.field_key) byKeyTier[d.field_key] = Number(d.tier_level) || 1
    })

    const reserved = new Set(['id', 'name', 'product_id', 'tier_1_data', '_id'])
    const t1 = {}, t2 = {}, t3 = {}, t4 = {}

    Object.keys(product).forEach(key => {
        if (reserved.has(key)) return
        const val = product[key]
        if (val == null || val === '') return
        const text = typeof val === 'object' && val.$date ? val.$date : String(val)
        let tier = byKeyTier[key]
        if (!tier) {
            if (key.endsWith('_date') || key === 'location') tier = 4
            else if (['lot_number', 'serial_number', 'batch_number'].includes(key)) tier = 3
            else if (['model_number', 'version', 'udi', 'sku', 'po_number', 'material_number', 'device_size',
                'issuing_agency', 'secondary_di_number', 'package_di_number', 'unit_of_use_di',
                'product_description'].includes(key)) tier = 2
            else tier = 1
        }
        let fieldKey = key
        if (key === 'manufactured_start_date') fieldKey = 'manufacturing_start_date'
        if (key === 'manufactured_end_date') fieldKey = 'manufacturing_end_date'

        if (tier === 1) t1[fieldKey] = text
        else if (tier === 2) t2[fieldKey] = text
        else if (tier === 3) t3[fieldKey] = text
        else t4[fieldKey] = text
    })

    const productId = product.product_id || null
    const result = {
        name: product.name || '',
        tier_1_data: [{
            id: newTierId(),
            values: t1,
            tier_2_data: [{
                id: newTierId(),
                values: t2,
                tier_3_data: [{
                    id: newTierId(),
                    values: t3,
                    tier_4_data: [{ id: newTierId(), values: t4 }]
                }]
            }]
        }]
    }

    if (productId != null && Number(productId) > 0) {
        result.product_id = Number(productId)
    }

    // Prefer existing Mongo UUID; otherwise generate
    if (typeof product.id === 'string' && product.id && !/^\d+$/.test(product.id.trim())) {
        result.id = product.id
    } else if (product.id != null && Number(product.id) > 0 && /^\d+$/.test(String(product.id).trim())) {
        result.product_id = result.product_id || Number(product.id)
        result.id = newTierId()
    } else {
        result.id = newTierId()
    }

    return result
}

const communicationProductService = {
    getProducts(communicationId) {
        return http.get(`${API_ROOT}/provider/communicationProducts/${communicationId}`).then(resp => {
            return resp
        })
    },
    upsertProducts(communicationId, payload) {
        return http.post(`${API_ROOT}/provider/communicationProducts/${communicationId}/UpsertProducts`, payload).then(resp => {
            return resp
        })
    },
    deleteProduct(communicationId, productId) {
        return http.delete(`${API_ROOT}/provider/communicationProducts/${communicationId}/remove-product/${productId}`).then(resp => {
            return resp
        })
    },
    getFieldDefinitions() {
        return http.get(`${API_ROOT}/provider/communicationProducts/product-field-definitions`).then(resp => {
            return resp
        })
    }
}

export default communicationProductService
